import { call, all, put, delay, select } from 'redux-saga/effects';
import logger from '@tcp/core/src/utils/loggerInstance';
import { isOptimizedDeleteEnabled } from '@tcp/core/src/utils/utils';
import {
  setLoaderState,
  setSectionLoaderState,
} from '@tcp/core/src/components/common/molecules/Loader/container/Loader.actions';
import { getBundleData } from '@tcp/core/src/components/common/atoms/BundleTracker/container/BundleTracker.selector';
import injectCouponDynamicStore from '@tcp/core/src/components/features/CnC/common/organism/CouponAndPromos/container/CouponDynamicStore';
import { getCartOrderId, getCartOrderList } from './CartItemTile.selectors';
import CARTPAGE_CONSTANTS from '../CartItemTile.constants';
import {
  getIsBundleTrackerEnabled,
  getIsLoyaltyTieringEnabled,
} from '../../../../../reduxStore/selectors/session.selectors';
import BAG_PAGE_ACTIONS from '../../BagPage/container/BagPage.actions';
import BagPageSelectors from '../../BagPage/container/BagPage.selectors';
import { removeItem } from '../../../../../services/abstractors/SFCC/CartItemTile';
import {
  removeCartItemComplete,
  setApplePayCartLoadingState,
  setCartLoadingState,
} from './CartItemTile.actions';
// eslint-disable-next-line import/no-cycle
import { processMutationCachedDataSaga } from '../../BagPage/container/BagPage.saga';
import { handleServerSideErrorAPI } from '../../Checkout/container/CheckoutCommon.saga';
import { getCouponList } from '../../common/organism/CouponAndPromos/container/Coupon.actions';
import { setBundleData } from '../../../../common/atoms/BundleTracker/container/BundleTracker.actions';
import { setRemoveItemInProgress } from '../../SinglePageCheckout/container/SinglePageCheckout.actions';
import { getSetShippingValuesActn } from '../../Checkout/container/Checkout.action';

const isOptimizedDelete = isOptimizedDeleteEnabled();

export function* afterRemovingCartItem(isMoveToSFL = true) {
  if (isMoveToSFL) {
    yield put(BAG_PAGE_ACTIONS.setCartItemsUpdating({ isDeleting: true }));
  }
}

const getActions = (
  payload,
  labels,
  { orderItems, bundleDataItems, isBundleTracker, isLoyaltyTieringEnabled = false }
) => {
  const actions = [];
  if (payload.getUnavailableOOSItems && payload.getUnavailableOOSItems.length > 0) {
    const unavailableOOSItems = payload?.getUnavailableOOSItems;
    actions.push(call(removeItem, isLoyaltyTieringEnabled, unavailableOOSItems, labels));
  } else if (payload && payload.itemId) {
    actions.push(
      call(removeItem, isLoyaltyTieringEnabled, payload.itemId, labels, {
        orderItems,
        bundleDataItems,
        isBundleTracker,
      })
    );
  } else if (payload) {
    actions.push(
      call(removeItem, isLoyaltyTieringEnabled, payload, labels, {
        orderItems,
        bundleDataItems,
        isBundleTracker,
      })
    );
  }
  return actions;
};

/**
 *@function confirmRemoveItem to be invoked to delete item form cart
 *
 * @export
 * @param {*} { payload, afterHandler }
 */
export function* confirmRemoveItem({ payload, afterHandler, isMiniBag, isMoveToSFL }) {
  try {
    if (isMiniBag || payload.isMiniBag) {
      yield put(setSectionLoaderState({ miniBagLoaderState: true, section: 'minibag' }));
    } else {
      yield put(setLoaderState(true));
    }
    const orderItems = yield select(getCartOrderList);
    const bundleDataItems = yield select(getBundleData);
    const isBundleTracker = yield select(getIsBundleTrackerEnabled);
    const labels = yield select(BagPageSelectors.getErrorMapping);
    const isLoyaltyTieringEnabled = yield select(getIsLoyaltyTieringEnabled);
    const actions = getActions(payload, labels, {
      orderItems,
      bundleDataItems,
      isBundleTracker,
      isLoyaltyTieringEnabled,
    });
    const resp = yield all(actions);
    const {
      deleteItemResp = {},
      isLoyaltyBenefitsCacheUpdate = false,
      ...res
    } = resp[resp.length - 1];
    yield put(removeCartItemComplete(res));
    yield put(BAG_PAGE_ACTIONS.removeDeletedItem(res));
    if (afterHandler) {
      afterHandler();
    }
    const couponMap = deleteItemResp?.c_couponMap;
    yield call(processMutationCachedDataSaga, {
      payload: {
        respToProcess: deleteItemResp,
        onCartRes: yield call(afterRemovingCartItem, isMoveToSFL),
        skipStateUpdate: true,
        isLoyaltyBenefitsCacheUpdate,
      },
    });
    yield put(setRemoveItemInProgress(false));
    if (couponMap) {
      yield injectCouponDynamicStore();
      yield put(getCouponList({ ignoreCache: true, appliedCoupons: couponMap }));
    }
    yield put(setLoaderState(false));
    yield put(setSectionLoaderState({ miniBagLoaderState: false, section: 'minibag' }));
    yield put(BAG_PAGE_ACTIONS.setCartItemsUpdating({ isDeleting: false }));
    yield put(BAG_PAGE_ACTIONS.setCartItemsSFL(false));
  } catch (err) {
    const isMinibag = isMiniBag || payload.isMiniBag;
    yield put(setLoaderState(false));
    yield put(setRemoveItemInProgress(false));
    yield put(setSectionLoaderState({ miniBagLoaderState: false, section: 'minibag' }));
    logger.error(err);
    yield call(handleServerSideErrorAPI, err, CARTPAGE_CONSTANTS.CART_ITEM_TILE, isMinibag);
  }
}

/**
 *@function confirmRemoveItemInChannel to be invoked to delete item form cart inside
 *
 * @export
 * @param {*} { payload, afterHandler }
 */

function* handleShippingAddressIfNeeded(deleteItemResp) {
  const shipments = deleteItemResp?.shipments;
  const shippingAddress = shipments?.[0]?.shippingAddress;
  const hasShippingAddress = shippingAddress?.address1;
  if (!hasShippingAddress && shippingAddress) {
    yield put(getSetShippingValuesActn(shippingAddress));
  }
}

export function* confirmRemoveItemInChannel({
  payload,
  afterHandler,
  isMiniBag,
  isMoveToSFL,
  animationTimeOut,
}) {
  try {
    const isBundleTracker = yield select(getIsBundleTrackerEnabled);
    const orderItems = yield select(getCartOrderList);
    const bundleDataItems = yield select(getBundleData);
    if (isMiniBag || payload.isMiniBag) {
      yield put(setSectionLoaderState({ miniBagLoaderState: true, section: 'minibag' }));
    } else {
      yield put(setCartLoadingState(true));
    }
    if (animationTimeOut) {
      yield delay(animationTimeOut);
    }
    const deletedProductInfo = {
      orderId: yield select(getCartOrderId),
      orderItems: [payload],
    };
    yield put(BAG_PAGE_ACTIONS.removeDeletedItem(deletedProductInfo));
    const labels = yield select(BagPageSelectors.getErrorMapping);
    const isLoyaltyTieringEnabled = yield select(getIsLoyaltyTieringEnabled);
    const actions = getActions(payload, labels, {
      orderItems,
      bundleDataItems,
      isBundleTracker,
      isLoyaltyTieringEnabled,
    });
    const resp = yield all(actions);
    const {
      deleteItemResp = {},
      bundleData = [],
      isLoyaltyBenefitsCacheUpdate = false,
    } = resp[resp.length - 1];

    yield call(handleShippingAddressIfNeeded, deleteItemResp);
    yield put(setCartLoadingState(false));
    yield put(setBundleData(bundleData));

    if (afterHandler) {
      afterHandler();
    }
    const couponMap = deleteItemResp?.c_couponMap;
    yield call(processMutationCachedDataSaga, {
      payload: {
        respToProcess: deleteItemResp,
        onCartRes: yield call(afterRemovingCartItem, isMoveToSFL),
        skipStateUpdate: true,
        isLoyaltyBenefitsCacheUpdate,
      },
    });
    yield put(setRemoveItemInProgress(false));
    if (couponMap) {
      yield injectCouponDynamicStore();
      yield put(getCouponList({ ignoreCache: true, appliedCoupons: couponMap }));
    }
    yield put(setSectionLoaderState({ miniBagLoaderState: false, section: 'minibag' }));

    yield put(BAG_PAGE_ACTIONS.setCartItemsUpdating({ isDeleting: false }));
    yield put(BAG_PAGE_ACTIONS.setCartItemsSFL(false));
  } catch (error) {
    const isMinibag = isMiniBag || payload.isMiniBag;
    yield put(setCartLoadingState(false));
    yield put(setRemoveItemInProgress(false));
    yield put(setSectionLoaderState({ miniBagLoaderState: false, section: 'minibag' }));
    const errorMapping = yield select(BagPageSelectors.getErrorMapping);
    const errorMessage =
      // eslint-disable-next-line no-underscore-dangle
      (error && error.errorMessage && error.errorMessage._error) ||
      (error && error.message && errorMapping.TIMEOUT_ERROR) ||
      (errorMapping && errorMapping.DEFAULT) ||
      'ERROR';
    yield put(BAG_PAGE_ACTIONS.setCartItemsSflError(errorMessage));
    yield put(BAG_PAGE_ACTIONS.addDeletedItemOnAPiFailure());
    yield call(handleServerSideErrorAPI, error, CARTPAGE_CONSTANTS.CART_ITEM_TILE, isMinibag);
  }
}

/**
 *
 * @function removeCartItem to be called when item delete from cart is required it opens the confirmation modal if function is invoked from bag page and item is available
 * @export
 * @param {*} { payload }
 *
 */
// eslint-disable-next-line import/prefer-default-export
export function* removeCartItem(props) {
  const payload = props?.payload ? props?.payload : props;
  const { itemId, pageView, price, isMoveToSFL, animationTimeOut = 0 } = payload;
  yield put(setRemoveItemInProgress(true));
  yield put(setApplePayCartLoadingState(true));
  if (pageView === 'myBag') {
    if (!isOptimizedDelete) {
      yield call(confirmRemoveItem, { payload: itemId, price, isMoveToSFL });
      yield put(setRemoveItemInProgress(false));
    } else {
      yield call(confirmRemoveItemInChannel, {
        payload: itemId,
        price,
        isMoveToSFL,
        animationTimeOut,
      });
    }
    yield put(setRemoveItemInProgress(false));
    yield put(BAG_PAGE_ACTIONS.clearNonCachedCartDataTTL());
  } else if (!isOptimizedDelete) {
    yield call(confirmRemoveItem, {
      payload: itemId,
      isMiniBag: true,
      price,
      isMoveToSFL,
      isCheckoutFlow: true,
    });
    yield put(setRemoveItemInProgress(false));
  } else {
    yield call(confirmRemoveItemInChannel, {
      payload: itemId,
      isMiniBag: true,
      price,
      isMoveToSFL,
      isCheckoutFlow: true,
      animationTimeOut,
    });
    yield put(setRemoveItemInProgress(false));
  }
}
